import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

// ─── Colors ────────────────────────────────────────────────────
const COLORS = {
  blue: '#3b82f6', green: '#10b981', amber: '#f59e0b',
  purple: '#8b5cf6', red: '#ef4444', sky: '#0ea5e9',
};
const PIE_COLORS = [COLORS.blue, COLORS.green, COLORS.amber, COLORS.purple, COLORS.red, COLORS.sky];

// ─── Types ─────────────────────────────────────────────────────
type SectionKey = 'all' | 'gsc' | 'gmb' | 'searchEngines' | 'aiReferrals' | 'ga4' | 'ahrefs';

interface DateRange { from: string; to: string; }
interface ClientMeta { name: string; category: string; domain: string; }
interface SectionData<T> { data: T | null; loading: boolean; error: string | null; }

// ─── Utility ───────────────────────────────────────────────────
function toArray(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object' && Array.isArray(v.current)) return v.current;
  return [];
}
function fmt(n: number | undefined | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US');
}
function defaultDateRange(): DateRange {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = new Date(today);
  to.setDate(to.getDate() - 1); // yesterday (exclude incomplete today)
  const from = new Date(today);
  from.setDate(from.getDate() - 30); // 30 days back
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}
function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}
function changePct(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100 * 10) / 10;
}

// ─── MetricCard ────────────────────────────────────────────────
function MetricCard({ label, value, change, suffix }: {
  label: string; value: number | string | undefined | null;
  change?: number | null; suffix?: string;
}) {
  const formatted = typeof value === 'number' ? fmt(value) : (value ?? '—');
  const hasChange = change != null && isFinite(change);
  const positive = hasChange && change! >= 0;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">
        {formatted}{suffix ? <span className="text-sm font-normal text-gray-500 ml-1">{suffix}</span> : null}
      </p>
      {hasChange && (
        <span className={`inline-flex items-center mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {positive ? '↑' : '↓'} {Math.abs(change!).toFixed(1)}%
        </span>
      )}
    </div>
  );
}

// ─── DataTable ─────────────────────────────────────────────────
function DataTable<T extends Record<string, any>>({ columns, data, defaultSort }: {
  columns: { key: string; label: string; render?: (row: T) => React.ReactNode; align?: 'left' | 'right' }[];
  data: T[]; defaultSort?: { key: string; dir: 'asc' | 'desc' };
}) {
  const [sortKey, setSortKey] = useState(defaultSort?.key ?? columns[0]?.key ?? '');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSort?.dir ?? 'desc');
  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [data, sortKey, sortDir]);
  const toggleSort = (key: string) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map(col => (
              <th key={col.key}
                className={`py-2 px-3 font-medium text-gray-500 cursor-pointer hover:text-gray-800 select-none ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                onClick={() => toggleSort(col.key)}>
                {col.label}{sortKey === col.key && <span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={columns.length} className="py-6 text-center text-gray-400">No data</td></tr>}
          {sorted.map((row, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
              {columns.map(col => (
                <td key={col.key} className={`py-2 px-3 ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.render ? col.render(row) : String(row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────
function SectionSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-6 bg-gray-200 rounded w-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg" />)}
      </div>
      <div className="h-64 bg-gray-200 rounded-lg" />
    </div>
  );
}
function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <p className="text-red-600 font-medium">Failed to load</p>
      <p className="text-red-500 text-sm mt-1">{message}</p>
      <button onClick={onRetry} className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">Retry</button>
    </div>
  );
}
function SectionWrapper({ id, title, children, defaultOpen = true }: { id: string; title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between p-6 pb-0 text-left hover:bg-gray-50 rounded-t-xl transition-colors">
        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
        </span>
      </button>
      {open && <div className="p-6 pt-5 space-y-5">{children}</div>}
      {!open && <div className="h-2" />}
    </section>
  );
}
function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-semibold">{fmt(p.value)}</span></p>)}
    </div>
  );
}
function renderPieLabel({ name, percent }: { name: string; percent: number }) {
  if (percent < 0.03) return null;
  return `${name} ${(percent * 100).toFixed(0)}%`;
}

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════
// Error boundary wrapper
import { Component, type ErrorInfo, type ReactNode } from 'react';
class DetailErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('SEOClientDetail crash:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Page Error</h2>
          <pre className="text-sm text-red-500 bg-red-50 p-4 rounded-lg overflow-auto max-w-2xl mx-auto">{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function SEOClientDetailInner() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
  const [activeFilter, setActiveFilter] = useState<SectionKey>('all');
  const [clientMeta, setClientMeta] = useState<ClientMeta | null>(null);

  // Raw API responses
  const [gsc, setGsc] = useState<SectionData<any>>({ data: null, loading: true, error: null });
  const [gmb, setGmb] = useState<SectionData<any>>({ data: null, loading: true, error: null });
  const [ga4, setGa4] = useState<SectionData<any>>({ data: null, loading: true, error: null });
  const [ahrefs, setAhrefs] = useState<SectionData<any>>({ data: null, loading: true, error: null });

  // Cache state
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoRefreshTriggered = useRef(false);

  // ─── Fetchers ──────────────────────────────────────────────
  const fetchSection = useCallback(async (
    url: string,
    setter: (s: SectionData<any>) => void,
    params?: Record<string, string>,
  ) => {
    setter({ data: null, loading: true, error: null });
    try {
      const { data } = await api.get(url, { params, timeout: 50000 });
      if (data?.error) throw new Error(data.error);
      setter({ data, loading: false, error: null });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Request failed';
      setter({ data: null, loading: false, error: msg });
    }
  }, []);

  const dateParams = useMemo(() => ({ from: dateRange.from, to: dateRange.to }), [dateRange]);

  const fetchGSC = useCallback(() => fetchSection(`/seo-dashboard/gsc/${clientId}`, setGsc, dateParams), [clientId, dateParams, fetchSection]);
  const fetchGMB = useCallback(() => fetchSection(`/seo-dashboard/localvikings/${clientId}`, setGmb, dateParams), [clientId, dateParams, fetchSection]);
  const fetchGA4 = useCallback(() => fetchSection(`/seo-dashboard/ga4/${clientId}`, setGa4, dateParams), [clientId, dateParams, fetchSection]);
  const fetchAhrefs = useCallback(() => fetchSection(`/seo-dashboard/ahrefs/${clientId}`, setAhrefs), [clientId, fetchSection]);

  // Apply cached detail data to all sections at once
  const applyCachedData = useCallback((detail: any) => {
    if (detail.gsc && !detail.gsc.error) setGsc({ data: detail.gsc, loading: false, error: null });
    if (detail.ga4 && !detail.ga4.error) setGa4({ data: detail.ga4, loading: false, error: null });
    if (detail.lv && !detail.lv.error) setGmb({ data: detail.lv, loading: false, error: null });
    if (detail.ahrefs && !detail.ahrefs.error) setAhrefs({ data: detail.ahrefs, loading: false, error: null });
    if (detail.fetched_at) setCachedAt(detail.fetched_at);
  }, []);

  // Fetch fresh data for all sections via single endpoint (parallel on backend)
  const fetchAllFresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const { data } = await api.post(`/seo-dashboard/fetch-detail/${clientId}`, null, {
        params: { from: dateRange.from, to: dateRange.to },
        timeout: 100000,
      });
      applyCachedData(data);
    } catch (e: any) {
      console.error('Fresh fetch failed, falling back to individual fetches', e);
      // Fallback: fetch individually
      fetchGSC(); fetchGMB(); fetchGA4(); fetchAhrefs();
    } finally {
      setIsRefreshing(false);
    }
  }, [clientId, dateRange, applyCachedData, fetchGSC, fetchGMB, fetchGA4, fetchAhrefs]);

  // Fetch client meta
  useEffect(() => {
    api.get(`/seo-dashboard/clients/${clientId}`)
      .then(r => {
        const d = r.data;
        let domain = d.gsc_site_url || d.ahrefs_target || '';
        domain = domain.replace(/^(sc-domain:|https?:\/\/)/, '').replace(/\/+$/, '');
        setClientMeta({ name: d.name, category: d.category, domain });
      })
      .catch(() => setClientMeta({ name: clientId || '', category: 'unknown', domain: '' }));
  }, [clientId]);

  // Load: try cache first, then fetch fresh if stale or missing
  useEffect(() => {
    autoRefreshTriggered.current = false;
    let cancelled = false;

    async function loadData() {
      // Reset all to loading
      setGsc({ data: null, loading: true, error: null });
      setGmb({ data: null, loading: true, error: null });
      setGa4({ data: null, loading: true, error: null });
      setAhrefs({ data: null, loading: true, error: null });
      setCachedAt(null);

      try {
        // 1. Try cache first
        const { data: cacheResp } = await api.get(`/seo-dashboard/cached-detail/${clientId}`, {
          params: { from: dateRange.from, to: dateRange.to }, timeout: 10000,
        });

        if (!cancelled && cacheResp.cached && cacheResp.data) {
          // Show cached data immediately
          applyCachedData(cacheResp.data);

          // If stale (>24h), auto-refresh in background
          if (cacheResp.data.stale && !autoRefreshTriggered.current) {
            autoRefreshTriggered.current = true;
            setIsRefreshing(true);
            api.post(`/seo-dashboard/fetch-detail/${clientId}`, null, {
              params: { from: dateRange.from, to: dateRange.to }, timeout: 100000,
            }).then(({ data: fresh }) => {
              if (!cancelled) applyCachedData(fresh);
            }).catch(() => {}).finally(() => {
              if (!cancelled) setIsRefreshing(false);
            });
          }
          return;
        }
      } catch {
        // Cache miss or error — continue to fresh fetch
      }

      // 2. No cache — fetch fresh
      if (!cancelled) {
        setIsRefreshing(true);
        try {
          const { data: fresh } = await api.post(`/seo-dashboard/fetch-detail/${clientId}`, null, {
            params: { from: dateRange.from, to: dateRange.to }, timeout: 100000,
          });
          if (!cancelled) applyCachedData(fresh);
        } catch {
          // Fallback: fetch individually
          if (!cancelled) { fetchGSC(); fetchGMB(); fetchGA4(); fetchAhrefs(); }
        } finally {
          if (!cancelled) setIsRefreshing(false);
        }
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [clientId, dateRange, applyCachedData, fetchGSC, fetchGMB, fetchGA4, fetchAhrefs]);

  // Helper: how long ago was cache
  const cacheAgeText = useMemo(() => {
    if (!cachedAt) return null;
    const ms = Date.now() - new Date(cachedAt).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }, [cachedAt]);

  const show = (key: SectionKey) => activeFilter === 'all' || activeFilter === key;

  const SECTION_FILTERS: { key: SectionKey; label: string }[] = [
    { key: 'all', label: 'All' }, { key: 'gsc', label: 'GSC' }, { key: 'gmb', label: 'GMB' },
    { key: 'searchEngines', label: 'Search Engines' }, { key: 'aiReferrals', label: 'AI Referrals' },
    { key: 'ga4', label: 'GA4' }, { key: 'ahrefs', label: 'Ahrefs' },
  ];

  // ─── Transform backend data to chart-friendly format ───────
  // GSC
  const gscTimeseries = useMemo(() => toArray(gsc.data?.daily).map((r: any) => ({ date: r.key, clicks: r.clicks, impressions: r.impressions })), [gsc.data]);
  const gscTotals = gsc.data?.totals || {};
  const gscPrevTotals = gsc.data?.previousTotals || {};
  const gscQueries = useMemo(() => {
    const curr = toArray(gsc.data?.topQueries);
    const prev = toArray(gsc.data?.previousTopQueries);
    const prevMap = new Map(prev.map((p: any) => [p.key, p]));
    return curr.map((q: any) => {
      const p: any = prevMap.get(q.key);
      return { query: q.key, clicks: q.clicks, impressions: q.impressions,
        clicks_change: p ? q.clicks - p.clicks : null, impressions_change: p ? q.impressions - p.impressions : null };
    });
  }, [gsc.data]);
  const gscPages = useMemo(() => {
    const curr = toArray(gsc.data?.topPages);
    const prev = toArray(gsc.data?.previousTopPages);
    const prevMap = new Map(prev.map((p: any) => [p.key, p]));
    return curr.map((p: any) => {
      const pr: any = prevMap.get(p.key);
      return { page: p.key, clicks: p.clicks, impressions: p.impressions,
        clicks_change: pr ? p.clicks - pr.clicks : null, impressions_change: pr ? p.impressions - pr.impressions : null };
    });
  }, [gsc.data]);
  const gscImageClicks = gscTotals.imageClicks ?? gsc.data?.imageSearch?.totals?.clicks ?? 0;
  const gscPrevImageClicks = gscPrevTotals.imageClicks ?? gsc.data?.imageSearch?.previousTotals?.clicks ?? 0;

  // GMB / LV
  const gmbTimeseries = useMemo(() => toArray(gmb.data?.daily).map((r: any) => ({
    date: r.date || r.key, call_clicks: r.callClicks ?? r.call_clicks ?? 0, website_clicks: r.websiteClicks ?? r.website_clicks ?? 0,
    direction_requests: r.directionRequests ?? r.direction_requests ?? 0,
  })), [gmb.data]);
  const gmbTotals = gmb.data?.totals || {};
  const gmbPrevTotals = gmb.data?.previousTotals || {};
  const gmbPlatforms = useMemo(() => {
    const bd = gmb.data?.platformBreakdown;
    if (Array.isArray(bd)) return bd.map((p: any) => ({ platform: p.key || p.platform, value: p.value }));
    if (bd && typeof bd === 'object') return Object.entries(bd).map(([k, v]) => ({ platform: k, value: v as number }));
    return [];
  }, [gmb.data]);

  // GA4
  const ga4Timeseries = useMemo(() => toArray(ga4.data?.dailyUsers).map((r: any) => ({ date: r.key || r.date, users: r.totalUsers ?? r.users ?? 0 })), [ga4.data]);
  const ga4Totals = ga4.data?.totals || {};
  const ga4PrevTotals = ga4.data?.previousTotals || {};
  const ga4Sources = useMemo(() => toArray(ga4.data?.topSources).map((s: any) => ({ source: s.source || s.key || 'unknown', sessions: s.sessions ?? 0, users: s.users ?? 0 })), [ga4.data]);
  const ga4Cities = useMemo(() => toArray(ga4.data?.topCities).map((c: any) => ({ city: c.city || c.key || 'unknown', users: c.users ?? 0, sessions: c.sessions ?? 0 })), [ga4.data]);
  const ga4Devices = useMemo(() => {
    const bd = ga4.data?.deviceBreakdown;
    if (bd && typeof bd === 'object' && !Array.isArray(bd)) {
      // New format: {mobile, desktop, tablet}
      return Object.entries(bd).map(([device, value]) => ({ device, value: value as number }));
    }
    return toArray(bd).map((d: any) => ({ device: d.key || d.device || d.deviceCategory || 'unknown', value: d.users ?? d.value ?? 0 }));
  }, [ga4.data]);
  const ga4SearchEngines = useMemo(() => toArray(ga4.data?.searchEngines).map((s: any) => ({ engine: s.engine || s.key || 'unknown', sessions: s.sessions ?? 0 })), [ga4.data]);
  const ga4AIReferrals = useMemo(() => toArray(ga4.data?.aiReferrals).map((s: any) => ({ source: s.source || s.key || 'unknown', sessions: s.sessions ?? 0 })), [ga4.data]);

  // Ahrefs
  const ahrefsData = ahrefs.data || {};

  // ─── Render ────────────────────────────────────────────────
  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Header ──────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/seo-dashboard')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
              <span className="text-lg">&larr;</span> Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{clientMeta?.name ?? 'Loading...'}</h1>
              {clientMeta?.domain && <p className="text-sm text-gray-500 mt-0.5">{clientMeta.domain}</p>}
            </div>
            {clientMeta?.category && (
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                clientMeta.category.toLowerCase() === 'bh' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
              }`}>{clientMeta.category.toLowerCase() === 'bh' ? 'BH' : 'Non-BH'}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <label className="text-gray-500 font-medium">From</label>
            <input type="date" value={dateRange.from} onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <label className="text-gray-500 font-medium">To</label>
            <input type="date" value={dateRange.to} onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button
              onClick={fetchAllFresh}
              disabled={isRefreshing}
              className={`ml-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                isRefreshing
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
              }`}
            >
              {isRefreshing ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Refreshing...
                </span>
              ) : 'Refresh Data'}
            </button>
          </div>
        </div>

        {/* ── Section Filters ─────────────────────────────── */}
        <div className="flex flex-wrap gap-2">
          {SECTION_FILTERS.map(f => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                activeFilter === f.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}>{f.label}</button>
          ))}
        </div>

        {/* ── Cache Status ─────────────────────────────────── */}
        {cachedAt && (
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Cached {cacheAgeText}</span>
            {isRefreshing && <span className="text-blue-500 font-medium ml-1">— refreshing in background...</span>}
          </div>
        )}

        {/* ── GSC Section ─────────────────────────────────── */}
        {show('gsc') && (
          <SectionWrapper id="gsc" title="Google Search Console">
            {gsc.loading ? <SectionSkeleton /> : gsc.error ? (
              <SectionError message={gsc.error} onRetry={fetchGSC} />
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <MetricCard label="Total Clicks" value={gscTotals.clicks} change={changePct(gscTotals.clicks, gscPrevTotals.clicks)} />
                  <MetricCard label="Total Impressions" value={gscTotals.impressions} change={changePct(gscTotals.impressions, gscPrevTotals.impressions)} />
                  <MetricCard label="Image Traffic" value={gscImageClicks} change={changePct(gscImageClicks, gscPrevImageClicks)} />
                  <MetricCard label="Mobile Users" value={ga4.data?.deviceBreakdown?.mobile ?? null} change={ga4.data?.previousDeviceBreakdown ? changePct(ga4.data?.deviceBreakdown?.mobile ?? 0, ga4.data?.previousDeviceBreakdown?.mobile ?? 0) : null} />
                  <MetricCard label="Desktop Users" value={ga4.data?.deviceBreakdown?.desktop ?? null} change={ga4.data?.previousDeviceBreakdown ? changePct(ga4.data?.deviceBreakdown?.desktop ?? 0, ga4.data?.previousDeviceBreakdown?.desktop ?? 0) : null} />
                </div>
                {gscTimeseries.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">Clicks & Impressions Over Time</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={gscTimeseries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gscClicksGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} /><stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} /></linearGradient>
                          <linearGradient id="gscImpGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} /><stop offset="95%" stopColor={COLORS.green} stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<ChartTooltip />} /><Legend />
                        <Area yAxisId="left" type="monotone" dataKey="clicks" stroke={COLORS.blue} fill="url(#gscClicksGrad)" name="Clicks" />
                        <Area yAxisId="right" type="monotone" dataKey="impressions" stroke={COLORS.green} fill="url(#gscImpGrad)" name="Impressions" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {gscQueries.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Top Queries</p>
                    <DataTable data={gscQueries} defaultSort={{ key: 'clicks', dir: 'desc' }} columns={[
                      { key: 'query', label: 'Query' },
                      { key: 'clicks', label: 'Clicks', align: 'right', render: (r: any) => <span className="flex items-center justify-end gap-1.5">{fmt(r.clicks)}{r.clicks_change != null && <span className={`text-xs ${r.clicks_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>{r.clicks_change >= 0 ? '↑' : '↓'}{Math.abs(r.clicks_change)}</span>}</span> },
                      { key: 'impressions', label: 'Impressions', align: 'right', render: (r: any) => <span className="flex items-center justify-end gap-1.5">{fmt(r.impressions)}{r.impressions_change != null && <span className={`text-xs ${r.impressions_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>{r.impressions_change >= 0 ? '↑' : '↓'}{Math.abs(r.impressions_change)}</span>}</span> },
                    ]} />
                  </div>
                )}
                {gscPages.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Top Pages</p>
                    <DataTable data={gscPages} defaultSort={{ key: 'clicks', dir: 'desc' }} columns={[
                      { key: 'page', label: 'Page URL', render: (r: any) => <span className="text-blue-600 break-all text-xs" title={r.page}>{r.page.replace(/^https?:\/\/[^/]+/, '')}</span> },
                      { key: 'clicks', label: 'Clicks', align: 'right', render: (r: any) => <span className="flex items-center justify-end gap-1.5">{fmt(r.clicks)}{r.clicks_change != null && <span className={`text-xs ${r.clicks_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>{r.clicks_change >= 0 ? '↑' : '↓'}{Math.abs(r.clicks_change)}</span>}</span> },
                      { key: 'impressions', label: 'Impressions', align: 'right', render: (r: any) => <span className="flex items-center justify-end gap-1.5">{fmt(r.impressions)}{r.impressions_change != null && <span className={`text-xs ${r.impressions_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>{r.impressions_change >= 0 ? '↑' : '↓'}{Math.abs(r.impressions_change)}</span>}</span> },
                    ]} />
                  </div>
                )}
              </div>
            )}
          </SectionWrapper>
        )}

        {/* ── GMB Section ─────────────────────────────────── */}
        {show('gmb') && (
          <SectionWrapper id="gmb" title="Google Business Profile (Local Vikings)">
            {gmb.loading ? <SectionSkeleton /> : gmb.error ? (
              <SectionError message={gmb.error} onRetry={fetchGMB} />
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard label="Total Calls" value={gmbTotals.callClicks} change={changePct(gmbTotals.callClicks, gmbPrevTotals.callClicks)} />
                  <MetricCard label="Website Clicks" value={gmbTotals.websiteClicks} change={changePct(gmbTotals.websiteClicks, gmbPrevTotals.websiteClicks)} />
                  <MetricCard label="Direction Requests" value={gmbTotals.directionRequests} change={changePct(gmbTotals.directionRequests, gmbPrevTotals.directionRequests)} />
                  <MetricCard label="Impressions" value={gmbTotals.impressions ?? ((gmbTotals.desktopMaps || 0) + (gmbTotals.desktopSearch || 0) + (gmbTotals.mobileMaps || 0) + (gmbTotals.mobileSearch || 0))} change={gmbPrevTotals.impressions != null ? changePct(gmbTotals.impressions ?? 0, gmbPrevTotals.impressions ?? 0) : null} />
                </div>
                {gmbTimeseries.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-600 mb-3">Daily Trends</p>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={gmbTimeseries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                          <Tooltip content={<ChartTooltip />} /><Legend />
                          <Area type="monotone" dataKey="call_clicks" stroke={COLORS.sky} fill={COLORS.sky} fillOpacity={0.2} name="Calls" />
                          <Area type="monotone" dataKey="website_clicks" stroke={COLORS.green} fill={COLORS.green} fillOpacity={0.2} name="Clicks" />
                          <Area type="monotone" dataKey="direction_requests" stroke={COLORS.amber} fill={COLORS.amber} fillOpacity={0.2} name="Directions" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    {gmbPlatforms.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">Impressions by Platform</p>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={gmbPlatforms.filter((p: any) => p.value > 0)} dataKey="value" nameKey="platform" cx="50%" cy="50%" outerRadius={80} label={renderPieLabel}>
                          {gmbPlatforms.map((_: any, idx: number) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                    </div>
                  )}
                  </div>
                )}
              </div>
            )}
          </SectionWrapper>
        )}

        {/* ── Search Engines Section ──────────────────────── */}
        {show('searchEngines') && (
          <SectionWrapper id="search-engines" title="Other Search Engines">
            {ga4.loading ? <SectionSkeleton /> : ga4.error ? (
              <SectionError message={ga4.error} onRetry={fetchGA4} />
            ) : (
              <div className="space-y-5">
                <MetricCard label="Total Other SE Sessions" value={ga4Totals.otherSearchEngines} change={changePct(ga4Totals.otherSearchEngines, ga4PrevTotals.otherSearchEngines)} />
                {ga4SearchEngines.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">Sessions by Search Engine</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ga4SearchEngines}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="engine" tick={{ fontSize: 11 }} tickLine={false} /><YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} /><Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="sessions" name="Sessions" radius={[4, 4, 0, 0]}>{ga4SearchEngines.map((_: any, idx: number) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </SectionWrapper>
        )}

        {/* ── AI Referrals Section ────────────────────────── */}
        {show('aiReferrals') && (
          <SectionWrapper id="ai-referrals" title="AI Referrals">
            {ga4.loading ? <SectionSkeleton /> : ga4.error ? (
              <SectionError message={ga4.error} onRetry={fetchGA4} />
            ) : (
              <div className="space-y-5">
                <MetricCard label="Total AI Referral Sessions" value={ga4Totals.aiReferrals} change={changePct(ga4Totals.aiReferrals, ga4PrevTotals.aiReferrals)} />
                {ga4AIReferrals.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">AI Referral Distribution</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ga4AIReferrals} layout="vertical" margin={{ left: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} />
                        <YAxis type="category" dataKey="source" tick={{ fontSize: 11 }} tickLine={false} width={100} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="sessions" name="Sessions" radius={[0, 4, 4, 0]}>
                          {ga4AIReferrals.map((_: any, idx: number) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </SectionWrapper>
        )}

        {/* ── GA4 Section ─────────────────────────────────── */}
        {show('ga4') && (
          <SectionWrapper id="ga4" title="Google Analytics 4">
            {ga4.loading ? <SectionSkeleton /> : ga4.error ? (
              <SectionError message={ga4.error} onRetry={fetchGA4} />
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard label="Total Users" value={ga4Totals.totalUsers} change={changePct(ga4Totals.totalUsers, ga4PrevTotals.totalUsers)} />
                  <MetricCard label="Total Sessions" value={ga4Totals.sessions} change={changePct(ga4Totals.sessions, ga4PrevTotals.sessions)} />
                </div>
                {ga4Timeseries.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">Daily Users</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={ga4Timeseries}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} /><YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} /><Tooltip content={<ChartTooltip />} /><Area type="monotone" dataKey="users" stroke={COLORS.purple} fill={COLORS.purple} fillOpacity={0.2} name="Users" /></AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {ga4Sources.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Top Sources</p>
                      <DataTable data={ga4Sources} defaultSort={{ key: 'sessions', dir: 'desc' }} columns={[
                        { key: 'source', label: 'Source' },
                        { key: 'sessions', label: 'Sessions', align: 'right', render: (r: any) => fmt(r.sessions) },
                        { key: 'users', label: 'Users', align: 'right', render: (r: any) => fmt(r.users) },
                      ]} />
                    </div>
                  )}
                  {ga4Cities.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Top Cities</p>
                      <DataTable data={ga4Cities} defaultSort={{ key: 'users', dir: 'desc' }} columns={[
                        { key: 'city', label: 'City' },
                        { key: 'users', label: 'Users', align: 'right', render: (r: any) => fmt(r.users) },
                        { key: 'sessions', label: 'Sessions', align: 'right', render: (r: any) => fmt(r.sessions) },
                      ]} />
                    </div>
                  )}
                </div>
                {ga4Devices.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-3">Device Breakdown</p>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={ga4Devices}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="device" tick={{ fontSize: 12 }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="value" name="Users" radius={[4, 4, 0, 0]}>
                          {ga4Devices.map((_: any, idx: number) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </SectionWrapper>
        )}

        {/* ── Ahrefs Section ──────────────────────────────── */}
        {show('ahrefs') && (
          <SectionWrapper id="ahrefs" title="Ahrefs">
            {ahrefs.loading ? <SectionSkeleton /> : ahrefs.error ? (
              <SectionError message={ahrefs.error} onRetry={fetchAhrefs} />
            ) : (
              <div className="space-y-5">
                {ahrefsData.isStale && ahrefsData.fetchedAt && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                    Data may be stale — last updated {new Date(ahrefsData.fetchedAt).toLocaleDateString()}
                  </div>
                )}
                {ahrefsData.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                    {ahrefsData.error}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Domain Rating</p>
                    <div className="relative w-20 h-20">
                      <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                        <circle cx="18" cy="18" r="15.91" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                        <circle cx="18" cy="18" r="15.91" fill="none" stroke={COLORS.blue} strokeWidth="3"
                          strokeDasharray={`${ahrefsData.domainRating ?? 0} ${100 - (ahrefsData.domainRating ?? 0)}`} strokeLinecap="round" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-900">{ahrefsData.domainRating ?? '—'}</span>
                    </div>
                  </div>
                  <MetricCard label="Backlinks" value={ahrefsData.backlinks} />
                  <MetricCard label="Organic Keywords" value={ahrefsData.orgKeywords} />
                  <MetricCard label="Organic Traffic" value={ahrefsData.orgTraffic} />
                  <MetricCard label="Site Health" value={ahrefsData.healthScore} suffix="%" />
                </div>

                {/* Site Audit errors/warnings */}
                {(ahrefsData.siteErrors != null || ahrefsData.siteWarnings != null) && (
                  <div className="flex flex-wrap gap-3">
                    {ahrefsData.siteErrors != null && ahrefsData.siteErrors > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm">
                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                        <span className="text-red-700 font-medium">{fmt(ahrefsData.siteErrors)}</span>
                        <span className="text-gray-500">page errors</span>
                      </div>
                    )}
                    {ahrefsData.siteWarnings != null && ahrefsData.siteWarnings > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                        <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                        <span className="text-amber-700 font-medium">{fmt(ahrefsData.siteWarnings)}</span>
                        <span className="text-gray-500">page warnings</span>
                      </div>
                    )}
                    {ahrefsData.siteErrors === 0 && ahrefsData.siteWarnings === 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm">
                        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="text-green-700">No errors or warnings</span>
                      </div>
                    )}
                  </div>
                )}

                {ahrefsData.fetchedAt && <p className="text-xs text-gray-400">Last fetched: {new Date(ahrefsData.fetchedAt).toLocaleString()}</p>}

                {/* Pages Ranking table */}
                {ahrefsData.pagesRanking?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Pages Ranking (Top {Math.min(ahrefsData.pagesRanking.length, 100)})</p>
                    <DataTable data={ahrefsData.pagesRanking} defaultSort={{ key: 'traffic', dir: 'desc' }} columns={[
                      { key: 'url', label: 'URL', render: (r: any) => <span className="text-blue-600 break-all text-xs" title={r.url}>{r.url.replace(/^https?:\/\/[^/]+/, '')}</span> },
                      { key: 'traffic', label: 'Traffic', align: 'right', render: (r: any) => fmt(r.traffic) },
                      { key: 'keywords', label: 'Keywords', align: 'right', render: (r: any) => fmt(r.keywords) },
                      { key: 'position', label: 'Position', align: 'right', render: (r: any) => r.position > 0 ? r.position.toFixed(1) : '—' },
                    ]} />
                  </div>
                )}

                {/* Top Referring Domains table */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {ahrefsData.topReferringDomains?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">Top Referring Domains</p>
                      <DataTable data={ahrefsData.topReferringDomains} defaultSort={{ key: 'backlinks', dir: 'desc' }} columns={[
                        { key: 'domain', label: 'Domain' },
                        { key: 'dr', label: 'DR', align: 'right' },
                        { key: 'backlinks', label: 'Backlinks', align: 'right', render: (r: any) => fmt(r.backlinks) },
                      ]} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </SectionWrapper>
        )}
      </div>
    </Layout>
  );
}

export default function SEOClientDetailPage() {
  return (
    <DetailErrorBoundary>
      <SEOClientDetailInner />
    </DetailErrorBoundary>
  );
}
